import _ from 'lodash'
import wrapModel from '../components/wrap-model'
import Dataset from '../datasets/model/dataset'
import { isJunctionLinkField } from './dataset-field-filter'
import {getDialogDatasetOrDataset} from './edit/pages-edit'

/**
 * Find all datasets that can be selected when adding/editing a field on a form
 *
 * @param {Form} form the form to find datasets for
 * @param {Number} currentlySelectedDatasetId the dataset id that is currently
 * selected; passing this in will preserve this dataset in the list
 * @return {Dataset[]} available datasets to select from
 */
function getSelectableDatasets(form, currentlySelectedDatasetId) {
	const mainDataset = getDialogDatasetOrDataset(form)

	// #46722 get a list of all datasets and how many times they have been
	// used on this form
	const datasetUsesCounts = _.countBy(form.get('widgets'), w => {
		if (w.valueLabelFieldId) {
			return mainDataset.combinedFieldsById[w.valueLabelFieldId]?.datasetId
		}
		return null
	})

	// Figure out how many times each dataset can be referenced (in the case
	// where multiple main dataset fields link to the same dataset)
	const availableDatasetUsesCounts = _.countBy(mainDataset.combinedFields, f => {
		if (f.datasetId != mainDataset.id && f.linkedDatasetId == mainDataset.id) {
			return f.datasetId
		}
		if (f.linkedDatasetId) {
			return f.linkedDatasetId
		}
		return null
	})

	const linkedDatasets = _.filter(_.values(mainDataset.datasetsById), d => {
		// We put the main dataset in front, so remove it from this list
		if (d.id == mainDataset.id) return false
		// If this is already selected, that means we should allow it in the list
		if (d.id == currentlySelectedDatasetId) return true
		// Don't show junction datasets
		if (d.get('junctionDataset')) return false
		// Dataset doesn't link in some way to the main dataset
		if (_.isNil(findLinkField(form, d))) return false
		// We are already using this dataset as many times as we can on the form
		const usedCount = datasetUsesCounts[d.id]
		const availableCount = availableDatasetUsesCounts[d.id] || 1
		if (usedCount >= availableCount) return false

		return true
	})

	// Make sure the main dataset is always first
	return _.concat(mainDataset,
		..._.sortBy(linkedDatasets, (d) => d.get('label').toLocaleUpperCase()))
}

/**
 * Find all fields that can still be added to a form
 */
function getAvailableFields(form, dataset = null, originallySelected = null) {
	const usedFields = _.map(form.get('widgets'), w => {
		if (w.valueLabelFieldId) {
			return w.fieldId
		}
		return null
	}).filter(f => !_.isNil(f) && f != originallySelected)

	const fields = dataset ?
		dataset.get('fields') : getDialogDatasetOrDataset(form).get('fields')
	return _.filter(_.clone(fields), f =>
		!_.includes(usedFields, f.id))
}

/**
 * Given 2 datasets, find a field that links the two. This can be any of the
 * following scenarios:
 *
 * 1. The main dataset has a dataset-link to the linked dataset
 * 2. The linked dataset has a dataset-link to the main dataset
 * 3. The datasets are linked via a junction dataset
 *
 * @param {Form} form the form we are working with
 * @param {Dataset} dataset the dataset we are finding a linked field to
 */
function findLinkField(form, dataset, originallySelected = null) {
	const mainDataset = getDialogDatasetOrDataset(form)
	const linkedDataset = wrapModel(dataset, Dataset)

	const availableMainFields =
		getAvailableFields(form, null, originallySelected)

	// Check if the main dataset has a linked field to the selected dataset
	let field = _.find(availableMainFields, f =>
		f.linkedDatasetId === linkedDataset.id)
	if (field) return field

	const availableLinkFields =
		getAvailableFields(form, dataset, originallySelected)

	// Check if the dataset has a linked field to the main dataset
	field = _.find(availableLinkFields, f =>
		f.linkedDatasetId === mainDataset.id)
	if (field) return field

	// Check if the dataset is linked via a junction dataset
	const { fields, primaryKeyFieldId } = linkedDataset.attributes
	const pkField = _.find(fields, f => f.id === primaryKeyFieldId)
	if (pkField && isJunctionLinkField(pkField, form.dataset)) return pkField

	return null
}

export {
	getSelectableDatasets,
	findLinkField,
	getAvailableFields
}